import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useModelManagementStore from "../store/useModelManagementStore";
import useModelManagementHook from "../hooks/useModelManagementHook";
import ModelPopup from "../components/ModelPopup";
import DropdownPortal from "../../../../components/Atoms/DropdownPortal";
import TrainingMetricsPanel from "../components/TrainingMetricsPanel";
import { useSwal } from "../../../../components/Notifications/swal/SwalCom";
import { api_labelflow_training } from "../../../../config.jsx";
import "../styles/version.css";

const NAVIGATION_TARGET = {
  classification: "/ClassificationLabel",
  bounding_box: "/DetectionLabel",
  segmentation: "/SegmentationLabel",
};

const STORAGE_KEYS = {
  classification: {
    model: 'lf-classification-model',
    version: 'lf-classification-version-id',
  },
  bounding_box: {
    model: 'lf-detection-model',
    version: 'lf-detection-version-id',
  },
  segmentation: {
    model: 'lf-segmentation-model',
    version: 'lf-segmentation-version-id',
  },
};

const RESOLVE_KEYS = (value) => Array.isArray(value) ? value : [value];

const resolveTrainingParam = (version, candidateKeys, fallback) => {
  if (!version) return fallback;
  const params = version.trainingParams || {};
  const candidates = RESOLVE_KEYS(candidateKeys);
  for (const key of candidates) {
    if (params[key] !== undefined && params[key] !== null) {
      return params[key];
    }
    if (version[key] !== undefined && version[key] !== null) {
      return version[key];
    }
  }
  return fallback;
};

// Module scope so the live-log effect can depend on its result without pulling
// a component-scoped identity into the dependency array.
const resolveModelTypeForProgress = (model) => {
  const rawType = (model?.model_type || model?.modelType || "").toString().toLowerCase();
  if (!rawType) return null;
  if (rawType === "classification") return "classification";
  if (["detection", "bounding_box", "object_detection", "yolo_detection"].includes(rawType)) {
    return "detection";
  }
  if (["segmentation", "segment"].includes(rawType)) {
    return "segmentation";
  }
  return null;
};

const normalizeStatus = (status) =>
  (status || "").toString().toLowerCase().replace(/[\s_-]/g, "");

// A run that is still moving. Drives the live/archive wording so a finished or
// failed run is not labelled "Live".
const isLiveStatus = (status) =>
  ["queued", "inprogress", "training", "running"].includes(normalizeStatus(status));

// Statuses that HAVE a training record, and therefore logs worth opening.
//
// The progress popup used to be gated to `inProgress` alone, which made a
// failed run's logs unreachable from the UI -- exactly the case the backend
// retains them 24h for (see LOG_EXPIRY_SECONDS in publisher.py) and the case
// `seedHistory` was written to serve. A version nobody has trained has no job
// and no logs, so that one still gets no button.
const hasTrainingRecord = (status) =>
  isLiveStatus(status) ||
  ["complete", "completed", "partiallycomplete", "failed", "error", "cancelled", "canceled"].includes(
    normalizeStatus(status),
  );

// Score a completed version for the "Best" badge.
//
// This reads the SAME key the table's first metric column shows, deliberately:
// a badge the user cannot verify against the numbers in front of them is worse
// than no badge. It must never fall back to a key the pipeline does not write --
// the previous `performanceMetrics.accuracy` is absent from every model type
// (classification writes top1_acc/top5_acc/train_loss, detection and
// segmentation write ultralytics' `metrics/<name>(B|M)` plus fitness), so every
// version scored 0 and the badge silently landed on the first completed one.
const resolveVersionScore = (version, isClassification) => {
  const metrics = version?.performanceMetrics;
  if (!metrics || typeof metrics !== "object") return null;
  const raw = isClassification
    ? metrics.top1_acc ?? metrics.accuracy
    : metrics["metrics/mAP50(B)"] ?? metrics.mAP50 ?? metrics.mAP;
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
};

// Clock-only stamp for "when did this panel last read the server". Without it
// a Refresh on a finished run changes nothing on screen and reads as a dead
// button, since a completed version's metrics never change again.
const formatClockTime = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleTimeString();
  } catch (error) {
    return null;
  }
};

const formatBooleanDisplay = (value) => {
  if (value === undefined || value === null) return "-";
  if (typeof value === "string") {
    const normalized = value.toLowerCase();
    if (["true", "1", "yes"].includes(normalized)) return "Yes";
    if (["false", "0", "no"].includes(normalized)) return "No";
  }
  return value ? "Yes" : "No";
};

const ModelManagementMainSection = () => {
  const { projects, isModalOpen, setIsModalOpen, setModelInfo, projectId: activeProjectId } = useModelManagementStore();
  const { handler, downloadingModelId } = useModelManagementHook();
  const { swalFail, swalConfirm } = useSwal();

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [liveModal, setLiveModal] = useState({
    open: false,
    loading: false,
    error: null,
    payload: null,
    meta: null,
    fetchedAt: null,
  });
  const [liveLogs, setLiveLogs] = useState([]);
  const [liveLogsError, setLiveLogsError] = useState(null);
  const [isFetchingLogs, setIsFetchingLogs] = useState(false);
  // Bumped to force the live-log effect to re-subscribe. Refresh only touches
  // it when the socket is actually gone -- see handleRefreshLiveView.
  const [logStreamNonce, setLogStreamNonce] = useState(0);
  const websocketRef = useRef(null);
  const liveLogsEndRef = useRef(null);
  // The job id is passed on the log requests but does NOT select the Redis
  // channel (build_channel keys on model type + version only), so it must not
  // be an effect dependency -- a refresh that discovers the id would otherwise
  // drop and rebuild a healthy socket. A ref keeps the latest value reachable
  // at connect time without re-running the effect.
  const liveJobIdRef = useRef(null);
  const [configModal, setConfigModal] = useState({
    open: false,
    version: null,
    modelType: null,
  });
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownAnchor, setDropdownAnchor] = useState(null);
  const dropdownMenuRef = useRef(null);
  const fetchModels = handler.handleGetModels;

  // Close dropdown when clicking outside (including portal dropdown)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        openDropdownId &&
        !event.target.closest(".labelflow-dropdown-trigger") &&
        !event.target.closest(".labelflow-dropdown-portal")
      ) {
        setOpenDropdownId(null);
        setDropdownAnchor(null);
      }
    };

    const handleScroll = () => {
      if (openDropdownId) {
        setOpenDropdownId(null);
        setDropdownAnchor(null);
      }
    };

    if (openDropdownId) {
      document.addEventListener("click", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
      return () => {
        document.removeEventListener("click", handleClickOutside);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [openDropdownId]);

  // Initial load and periodic refresh with dynamic interval
  // Faster refresh (5s) when there are queued/inProgress tasks, slower (20s) otherwise
  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;
    
    const scheduleNextRefresh = () => {
      // Clear any existing timeout first
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      
      if (cancelled) return;
      
      // Get current projects from store directly
      const currentProjects = useModelManagementStore.getState().projects;
      
      // Check if there are any queued or inProgress tasks
      const hasActiveTasks = Array.isArray(currentProjects) && currentProjects.some((project) =>
        (project.models || []).some((model) =>
          (model.versions || []).some(ver => {
            const status = (ver?.trainingStatus || "").toLowerCase();
            return status === "queued" || status === "inprogress" || status === "training";
          })
        )
      );
      
      // Use faster refresh (5 seconds) if there are active tasks, otherwise 20 seconds
      const refreshInterval = hasActiveTasks ? 5000 : 20000;
      
      timeoutId = setTimeout(async () => {
        if (cancelled) return;
        
        try {
          const params = activeProjectId ? { project_id: activeProjectId } : undefined;
          await fetchModels(params);
        } catch (e) {
          // noop
        } finally {
          // Schedule next refresh after fetch completes
          scheduleNextRefresh();
        }
      }, refreshInterval);
    };
    
    const tick = async (showLoader = false) => {
      try {
        if (showLoader) {
          setIsLoading(true);
        }
        const params = activeProjectId ? { project_id: activeProjectId } : undefined;
        await fetchModels(params);
      } catch (e) {
        // noop
      } finally {
        if (!cancelled && showLoader) {
          setIsLoading(false);
        }
      }
    };
    
    // Initial load with loader
    tick(true).then(() => {
      // Start periodic refresh after initial load completes
      if (!cancelled) {
        scheduleNextRefresh();
      }
    });
    
    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  }, [fetchModels, activeProjectId]);

  const buildNavigationTarget = (modelType) => {
    const normalized = modelType?.toLowerCase();
    return NAVIGATION_TARGET[normalized] || null;
  };

  const persistSelection = (model, versionId) => {
    const type = model?.model_type || model?.modelType;
    const normalized = type?.toLowerCase();
    const keys = STORAGE_KEYS[normalized];
    if (!keys) return;
    try {
      sessionStorage.setItem(keys.model, JSON.stringify(model));
      if (versionId) {
        sessionStorage.setItem(keys.version, versionId);
      } else {
        sessionStorage.removeItem(keys.version);
      }
    } catch (err) {
      console.warn('Unable to persist selected model/version', err);
    }
  };

  const handleAddData = (model) => {
    persistSelection(model, null);
    setModelInfo(model);
    const destination = buildNavigationTarget(model?.model_type);
    if (!destination) {
      swalFail("Unsupported model type", "This model cannot be opened because its type is not supported.");
      return;
    }
    navigate(destination, { state: { model } });
  };

  const handleEditData = (model, versionId) => {
    persistSelection(model, versionId);
    setModelInfo(model);
    const destination = buildNavigationTarget(model?.model_type || model?.modelType);
    if (!destination) {
      swalFail("Unsupported model type", "This model cannot be opened because its type is not supported.");
      return;
    }
    navigate(destination, { state: { versionId, model } });
  };

  const handleEditModel = (model) => {
    setModelInfo(model);
    handler.handleOpenModal("edit");
  };

  const deriveJobId = (version) =>
    version?.latestTrainingJobId
    || version?.lastTrainingJobId
    || version?.trainingJobId
    || version?.jobId
    || null;

  const requestTrainingProgress = async ({ model, version, keepOpen = false }) => {
    if (!model || !version?._id) return;
    const jobId = deriveJobId(version);
    const modelTypeForApi = resolveModelTypeForProgress(model);

    if (!modelTypeForApi) {
      setLiveModal({
        open: true,
        loading: false,
        error: "Unsupported model type. Only classification, detection, or segmentation is allowed.",
        payload: null,
        meta: { model, version, jobId },
        fetchedAt: null,
      });
      return;
    }
    const resolvedJobId = jobId ?? (keepOpen ? liveJobIdRef.current ?? null : null);
    const nextMeta = { model, version, jobId: resolvedJobId, modelType: modelTypeForApi };
    liveJobIdRef.current = resolvedJobId;

    if (!keepOpen) {
      setLiveModal({
        open: true,
        loading: true,
        error: null,
        payload: null,
        meta: nextMeta,
        fetchedAt: null,
      });
    } else {
      setLiveModal((prev) => ({
        ...prev,
        open: true,
        loading: true,
        error: null,
        meta: nextMeta,
      }));
    }

    try {
      const data = await handler.handleGetTrainingProgress({
        modelType: modelTypeForApi,
        modelDataInfoId: version._id,
        jobId: resolvedJobId ?? undefined,
      });
      const jobIdFromApi = data?.job?.job_id || null;
      const metaWithJob = jobIdFromApi ? { ...nextMeta, jobId: jobIdFromApi } : nextMeta;
      liveJobIdRef.current = metaWithJob.jobId ?? null;
      setLiveModal((prev) => ({
        ...prev,
        open: true,
        loading: false,
        error: null,
        payload: data,
        meta: metaWithJob,
        fetchedAt: new Date(),
      }));
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message || "Failed to load training progress.";
      setLiveModal((prev) => ({
        ...prev,
        open: true,
        loading: false,
        error: detail,
        payload: null,
        meta: nextMeta,
        fetchedAt: new Date(),
      }));
    }
  };

  const handleOpenLiveView = (model, version) => {
    setLiveLogs([]);
    setLiveLogsError(null);
    liveJobIdRef.current = null;
    requestTrainingProgress({ model, version, keepOpen: false });
  };

  const handleRefreshLiveView = () => {
    if (!liveModal?.meta) return;
    const { model, version } = liveModal.meta;
    requestTrainingProgress({ model, version, keepOpen: true });

    // Revive the log stream only when it is actually down (training finished
    // and the server closed the subscription, or the connection dropped).
    // Re-subscribing a healthy socket would clear the panel and replay history
    // for no reason -- which is what made Refresh look like it broke the logs.
    const socket = websocketRef.current;
    const alive =
      socket &&
      (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING);
    if (!alive) {
      setLogStreamNonce((nonce) => nonce + 1);
    }
  };

  const handleCloseLiveView = () => {
    setLiveModal({
      open: false,
      loading: false,
      error: null,
      payload: null,
      meta: null,
      fetchedAt: null,
    });
    setLiveLogs([]);
    setLiveLogsError(null);
    liveJobIdRef.current = null;
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString();
    } catch (error) {
      return value;
    }
  };

  // Auto-scroll live logs to the bottom when new lines arrive and modal is open
  useEffect(() => {
    if (!liveModal.open) return;
    try {
      if (liveLogsEndRef.current) {
        liveLogsEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    } catch (e) {
      // no-op
    }
  }, [liveLogs, liveModal.open]);

  const openConfigModal = (version, modelType) => {
    setConfigModal({
      open: true,
      version,
      modelType,
    });
  };

  const closeConfigModal = () => {
    setConfigModal({
      open: false,
      version: null,
      modelType: null,
    });
  };

  // The identity of the live log stream, reduced to primitives. The Redis
  // channel is derived from (model type, version id) alone, so those two are
  // the whole subscription key -- everything else on `liveModal.meta` (a fresh
  // object on every progress fetch) must stay out of the effect's deps.
  const liveStreamModelType = liveModal.open
    ? liveModal.meta?.modelType || resolveModelTypeForProgress(liveModal.meta?.model) || null
    : null;
  const liveStreamVersionId = liveModal.open ? liveModal.meta?.version?._id ?? null : null;

  useEffect(() => {
    if (!liveStreamModelType || !liveStreamVersionId) {
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }
      return undefined;
    }

    const modelType = liveStreamModelType;
    const versionId = liveStreamVersionId;

    setIsFetchingLogs(true);
    setLiveLogs([]);
    setLiveLogsError(null);

    // `cancelled` guards the async history fetch below: the effect can be torn
    // down (modal closed, different version picked) while it is still in flight.
    let cancelled = false;

    // Seed the panel with the lines Redis already has, then subscribe for new
    // ones. The socket only carries lines published while it is open, so
    // without this the panel is empty whenever it is opened after training
    // started -- and permanently empty for a run that already finished or
    // failed, which is the common case for a run that dies in a few seconds.
    // History first, then subscribe: this can miss a line published in the
    // millisecond gap between the two, which is preferable to the duplicated
    // overlap the reverse order produces.
    const seedHistory = async () => {
      try {
        const data = await handler.handleGetTrainingLogs({
          modelType,
          modelDataInfoId: versionId,
          jobId: liveJobIdRef.current || undefined,
        });
        if (cancelled) return;
        const history = Array.isArray(data?.logs) ? data.logs : [];
        if (history.length) {
          setLiveLogs((prev) => [...history, ...prev].slice(-400));
          setIsFetchingLogs(false);
        }
      } catch (err) {
        // A missing history is not fatal -- live streaming can still work.
        console.error("Failed to load training log history:", err);
      }
    };

    // Resolve against the current page so a relative base (e.g. "/api_labelflow_training",
    // used when served same-origin behind an HTTPS reverse proxy) works too. Appending to
    // the existing path keeps any base-route prefix intact instead of overwriting it.
    const rawBase = (api_labelflow_training || "").replace(/\/$/, "");
    const url = new URL(
      `${rawBase}/streaming_management/ws/training-logs`,
      window.location.href
    );
    // Derive ws/wss from the resolved URL's protocol (https -> wss) so an HTTPS
    // page never opens an insecure ws:// socket (which the browser would block).
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    url.searchParams.set("modelType", modelType);
    url.searchParams.set("modelDataInfoId", versionId);
    if (liveJobIdRef.current) {
      url.searchParams.set("jobId", liveJobIdRef.current);
    }

    let socket = null;

    const connect = () => {
      if (cancelled) return;

      const sock = new WebSocket(url.toString());
      socket = sock;
      websocketRef.current = sock;

      sock.onopen = () => {
        setIsFetchingLogs(false);
      };

      sock.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.type === "error") {
            setLiveLogsError(payload.message || "A WebSocket error occurred.");
            return;
          }
          if (payload?.message) {
            setIsFetchingLogs(false);
            setLiveLogs((prev) => {
              const next = [...prev, payload.message];
              if (next.length > 400) {
                return next.slice(-400);
              }
              return next;
            });
          }
        } catch (err) {
          setLiveLogsError(`Failed to parse WebSocket message: ${err.message}`);
        }
      };

      sock.onerror = (event) => {
        console.error("WebSocket error:", event);
        setLiveLogsError("An error occurred while connecting to live logs.");
        setIsFetchingLogs(false);
      };

      sock.onclose = () => {
        setIsFetchingLogs(false);
        websocketRef.current = null;
      };
    };

    // Seed first, then subscribe -- see the note above seedHistory.
    seedHistory().finally(connect);

    return () => {
      cancelled = true;
      if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        socket.close();
      }
      websocketRef.current = null;
    };
    // Primitives only: a refresh re-fetches progress and hands back a new
    // `meta` object, and keying on that object tore this subscription down --
    // clearing the log panel -- once or twice per Refresh click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveStreamModelType, liveStreamVersionId, logStreamNonce]);

  const renderStatusBadge = (status, tooltipText) => {
    const normalized = (status || "pending").toString().toLowerCase();
    const sanitized = normalized.replace(/[\s_-]/g, "");

    const variants = {
      error: { className: "status-badge status-badge--error", label: "Error" },
      inprogress: { className: "status-badge status-badge--training", label: "Training" },
      training: { className: "status-badge status-badge--training", label: "Training" },
      running: { className: "status-badge status-badge--training", label: "Training" },
      complete: { className: "status-badge status-badge--complete", label: "Completed" },
      completed: { className: "status-badge status-badge--complete", label: "Completed" },
      success: { className: "status-badge status-badge--complete", label: "Completed" },
      pending: { className: "status-badge status-badge--pending", label: "Pending" },
      queued: { className: "status-badge status-badge--queued", label: "In Queue" },
      inqueue: { className: "status-badge status-badge--queued", label: "In Queue" },
      intrainingqueue: { className: "status-badge status-badge--queued", label: "In Queue" },
    };

    const variant = variants[sanitized] || {
      className: "status-badge status-badge--pending",
      label: status || "Pending",
    };

    return (
      <span className={variant.className} title={tooltipText || undefined}>
        {variant.label}
      </span>
    );
  };

  const renderBestBadge = (versionId, bestVersionId) => {
    if (versionId !== bestVersionId) return null;
    return (
      <span className="badge badge-success ml-1">
        Best
      </span>
    );
  };

  const formatMetricValue = (value) => {
    if (typeof value !== "number" || Number.isNaN(value)) {
      return "-";
    }
    return value.toFixed(3);
  };

  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    if (!activeProjectId) return projects;
    return projects.filter((project) => {
      const projectId = project._id || project.id || project.project_id || project.projectId;
      return String(projectId) === String(activeProjectId);
    });
  }, [projects, activeProjectId]);

  const flattenedProjects = useMemo(() => {
    if (!filteredProjects.length) return [];
    return filteredProjects.flatMap((project) =>
      (project.models || []).map((model) => ({ project, model })),
    );
  }, [filteredProjects]);

  const configModalValues = useMemo(() => {
    const version = configModal?.version;
    if (!version) {
      return {
        modelVariant: "-",
        epochs: "-",
        imgsz: "-",
        batchSize: "-",
        stratify: "-",
        trainSplit: "-",
        valSplit: "-",
        testSplit: "-",
        patience: "-",
      };
    }
    const modelVariant = resolveTrainingParam(version, ["modelName", "model_name"], "-");
    const epochs = resolveTrainingParam(version, ["epochs"], "-");
    const imgsz = resolveTrainingParam(version, ["imgsz", "imageSize", "image_size"], "-");
    const batchSize = resolveTrainingParam(version, ["batchSize", "batch_size"], "-");
    const stratifyRaw = resolveTrainingParam(version, ["stratify", "stratified"], undefined);
    const trainSplit = resolveTrainingParam(version, ["trainSplit", "train_split"], "-");
    const valSplit = resolveTrainingParam(version, ["valSplit", "val_split"], "-");
    const testSplit = resolveTrainingParam(version, ["testSplit", "test_split"], "-");
    const patience = resolveTrainingParam(version, ["patience"], "-");
    return {
      modelVariant: modelVariant ?? "-",
      epochs: epochs ?? "-",
      imgsz: imgsz ?? "-",
      batchSize: batchSize ?? "-",
      stratify: stratifyRaw,
      trainSplit: trainSplit ?? "-",
      valSplit: valSplit ?? "-",
      testSplit: testSplit ?? "-",
      patience: patience ?? "-",
    };
  }, [configModal]);

  if (isLoading) {
    return (
      <div className="model-mgmt-loading">
        <i className="fa fa-spinner fa-spin fa-3x mb-3 text-primary" aria-hidden />
        <p className="mb-0">Loading models…</p>
      </div>
    );
  }

  return (
    <div className="model-mgmt-section">
      {flattenedProjects.length > 0 ? (
        flattenedProjects.map(({ project, model }) => {
          const completedVersions = Array.isArray(model?.versions)
            ? model.versions.filter((ver) => (ver?.trainingStatus || "").toLowerCase() === "complete").length
            : 0;
          // Named once: the <th> text and each cell's data-label (the column
          // name the stacked phone layout renders) must not drift apart.
          const isClassification =
            (model?.model_type || model?.modelType || "").toLowerCase() === "classification";
          const metricLabels = isClassification
            ? ["Top-1 Acc", "Top-5 Acc", "Train Loss"]
            : ["mAP@50", "Precision", "Recall"];
          // A version with no reported score is not eligible: with nothing to
          // compare, "Best" would just mean "first in the array".
          let bestVersionId = null;
          if (Array.isArray(model?.versions)) {
            let bestScore = null;
            model.versions.forEach((version) => {
              if ((version?.trainingStatus || "").toLowerCase() !== "complete") return;
              const score = resolveVersionScore(version, isClassification);
              if (score === null) return;
              if (bestScore === null || score > bestScore) {
                bestScore = score;
                bestVersionId = version._id;
              }
            });
          }

          return (
            <div className="model-mgmt-card" key={model.model_id}>
              <div className="card-body">
                <div className="model-mgmt-card__header">
                  <div className="model-mgmt-card__title">
                    <span className="model-mgmt-card__project">
                      <i className="fa fa-folder mr-1" aria-hidden /> {project.project_name}
                    </span>
                    <span className="model-mgmt-card__separator" aria-hidden>&gt;</span>
                    <i className="fa fa-cube mr-2" aria-hidden />
                    <span className="model-mgmt-card__model-name">{model.model_name}</span>
                  </div>
                  <div className="model-mgmt-card__badges">
                    <span className="badge badge-pill badge-secondary small">
                      {(model?.model_type || "").toUpperCase()} model
                    </span>
                    {completedVersions > 0 && (
                      <span className="badge badge-success">Done {completedVersions}</span>
                    )}
                    <div className="btn-group labelflow-dropdown-trigger">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary labelflow-dropdown-btn"
                        aria-haspopup="true"
                        aria-expanded={openDropdownId === `model-${model.model_id}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setOpenDropdownId(openDropdownId === `model-${model.model_id}` ? null : `model-${model.model_id}`);
                          setDropdownAnchor(
                            openDropdownId === `model-${model.model_id}`
                              ? null
                              : { id: `model-${model.model_id}`, rect, type: "model", model, project },
                          );
                        }}
                      >
                        <i className="fa fa-ellipsis-h" aria-hidden />
                      </button>
                      {openDropdownId === `model-${model.model_id}` && dropdownAnchor?.id === `model-${model.model_id}` && (
                        <>
                          <DropdownPortal
                            anchorRect={dropdownAnchor.rect}
                            menuRef={dropdownMenuRef}
                          >
                              <div className="labelflow-dropdown-menu">
                                <button
                                  type="button"
                                  className="labelflow-dropdown-item"
                                  onClick={() => {
                                    setOpenDropdownId(null);
                                    setDropdownAnchor(null);
                                    handleEditModel(model);
                                  }}
                                >
                                  <i className="fa fa-edit mr-2" aria-hidden /> Edit Model
                                </button>
                                <button
                                  type="button"
                                  className="labelflow-dropdown-item labelflow-dropdown-item--danger"
                                  onClick={() => {
                                    setOpenDropdownId(null);
                                    setDropdownAnchor(null);
                                    swalConfirm(
                                      "Delete model",
                                      "Are you sure you want to delete this model?",
                                      async (result) => {
                                        if (result?.isConfirmed) {
                                          await handler.handleDeleteModel(model);
                                        }
                                      },
                                    );
                                  }}
                                >
                                  <i className="fa fa-trash mr-2" aria-hidden /> Delete
                                </button>
                              </div>
                          </DropdownPortal>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {model?.versions?.length ? (
                  <div className="model-mgmt-table-wrapper">
                    <table className="model-mgmt-table">
                      <thead>
                        <tr>
                          <th>Version</th>
                          <th>Images</th>
                          <th>Classes</th>
                          <th>{metricLabels[0]}</th>
                          <th>{metricLabels[1]}</th>
                          <th>{metricLabels[2]}</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                          {model.versions.map((ver) => {
                            const total = ver.labelingStatus?.totalImages || 0;
                            const labeled = ver.labelingStatus?.labeledImages || 0;
                            let primaryMetric = "-";
                            let secondaryMetric = "-";
                            let tertiaryMetric = "-";

                            if (isClassification) {
                              primaryMetric = formatMetricValue(
                                ver.performanceMetrics?.top1_acc ??
                                  ver.performanceMetrics?.accuracy ??
                                  null,
                              );
                              secondaryMetric = formatMetricValue(
                                ver.performanceMetrics?.top5_acc ??
                                  null,
                              );
                              tertiaryMetric = formatMetricValue(
                                ver.performanceMetrics?.train_loss ??
                                  ver.performanceMetrics?.loss ??
                                  null,
                              );
                            } else {
                              primaryMetric = formatMetricValue(
                                ver.performanceMetrics?.["metrics/mAP50(B)"] ??
                                  ver.performanceMetrics?.mAP50 ??
                                  ver.performanceMetrics?.mAP ??
                                  null,
                              );
                              secondaryMetric = formatMetricValue(
                                ver.performanceMetrics?.["metrics/precision(B)"] ??
                                  ver.performanceMetrics?.precision ??
                                  null,
                              );
                              tertiaryMetric = formatMetricValue(
                                ver.performanceMetrics?.["metrics/recall(B)"] ??
                                  ver.performanceMetrics?.recall ??
                                  null,
                              );
                            }

                            return (
                              <tr
                                key={ver._id}
                                onClick={() => handleEditData(model, ver._id)}
                              >
                                <td className="model-mgmt-version" data-label="Version">
                                  <div className="model-mgmt-version__stack">
                                    <div className="model-mgmt-version__num">
                                      {ver.version} {renderBestBadge(ver._id, bestVersionId)}
                                    </div>
                                    <div className="model-mgmt-version__model">
                                      {ver.trainingParams?.modelName || ver.modelName || "-"}
                                    </div>
                                  </div>
                                </td>
                                <td className="model-mgmt-images" data-label="Images">
                                  {total} <span className="text-muted small">({labeled} labeled)</span>
                                </td>
                                <td className="model-mgmt-classes" data-label="Classes" title={Array.isArray(ver.classes) ? ver.classes.map((c) => c.name).join(", ") : ""}>
                                  {Array.isArray(ver.classes) ? ver.classes.map((c) => c.name).join(", ") : "-"}
                                </td>
                                <td className="model-mgmt-metric" data-label={metricLabels[0]}>{primaryMetric}</td>
                                <td className="model-mgmt-metric" data-label={metricLabels[1]}>{secondaryMetric}</td>
                                <td className="model-mgmt-metric" data-label={metricLabels[2]}>{tertiaryMetric}</td>
                                <td className="model-mgmt-status" data-label="Status">{renderStatusBadge(ver?.trainingStatus, ver?.trainingError)}</td>
                                <td className="model-mgmt-actions" data-label="Actions" onClick={(e) => e.stopPropagation()}>
                                  <div className="model-mgmt-actions__group">
                                    {hasTrainingRecord(ver?.trainingStatus) && (
                                      <button
                                        type="button"
                                        className={`btn btn-sm ${
                                          isLiveStatus(ver?.trainingStatus)
                                            ? "btn-outline-primary"
                                            : "btn-outline-secondary"
                                        }`}
                                        onClick={() => handleOpenLiveView(model, ver)}
                                        title={
                                          isLiveStatus(ver?.trainingStatus)
                                            ? "Live training progress"
                                            : "Training progress & logs"
                                        }
                                      >
                                        <i
                                          className={`fa ${
                                            isLiveStatus(ver?.trainingStatus)
                                              ? "fa-broadcast-tower"
                                              : "fa-file-alt"
                                          }`}
                                          aria-hidden
                                        />
                                      </button>
                                    )}
                                    {ver?.trainingStatus === "complete" && (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-primary"
                                          title="View Details"
                                          onClick={() =>
                                            navigate("/ModelTrainingResult", {
                                              state: {
                                                projectId: project._id || project.project_id || project.projectId,
                                                model,
                                                version: ver,
                                              },
                                            })
                                          }
                                        >
                                          <i className="fa fa-chart-line" aria-hidden />
                                        </button>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          title="Download Model"
                                          onClick={() =>
                                            handler.handleDownloadTrainedModel({
                                              model_type: model.model_type,
                                              model_data_info_id: ver._id,
                                              owner_id: model.owner_id || model.ownerId,
                                            })
                                          }
                                          disabled={downloadingModelId === ver._id}
                                        >
                                          {downloadingModelId === ver._id ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden />
                                          ) : (
                                            <i className="fa fa-download" aria-hidden />
                                          )}
                                        </button>
                                      </>
                                    )}
                                    <div className="btn-group labelflow-dropdown-trigger">
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary labelflow-dropdown-btn"
                                        aria-haspopup="true"
                                        aria-expanded={openDropdownId === ver._id}
                                        title="More actions"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          const nextId = openDropdownId === ver._id ? null : ver._id;
                                          setOpenDropdownId(nextId);
                                          setDropdownAnchor(
                                            nextId
                                              ? { id: ver._id, rect, type: "row", model, ver, project }
                                              : null,
                                          );
                                        }}
                                      >
                                        <i className="fa fa-ellipsis-h" aria-hidden />
                                      </button>
                                      {openDropdownId === ver._id && dropdownAnchor?.id === ver._id && (
                                        <>
                                          <DropdownPortal
                                            anchorRect={dropdownAnchor.rect}
                                            menuRef={dropdownMenuRef}
                                          >
                                              <div className="labelflow-dropdown-menu">
                                                <button
                                                  type="button"
                                                  className="labelflow-dropdown-item"
                                                  onClick={() => {
                                                    setOpenDropdownId(null);
                                                    setDropdownAnchor(null);
                                                    openConfigModal(ver, model?.model_type || model?.modelType);
                                                  }}
                                                >
                                                  <i className="fa fa-cog mr-2" aria-hidden /> View Config
                                                </button>
                                                <button
                                                  type="button"
                                                  className="labelflow-dropdown-item"
                                                  onClick={() => {
                                                    setOpenDropdownId(null);
                                                    setDropdownAnchor(null);
                                                    handler.handleDownloadImageData({
                                                      model_type: model.model_type,
                                                      model_data_info_id: ver._id,
                                                    });
                                                  }}
                                                >
                                                  <i className="fa fa-image mr-2" aria-hidden /> Download Images
                                                </button>
                                                <button
                                                  type="button"
                                                  className="labelflow-dropdown-item"
                                                  onClick={() => {
                                                    setOpenDropdownId(null);
                                                    setDropdownAnchor(null);
                                                    handleEditData(model, ver._id);
                                                  }}
                                                >
                                                  <i className="fa fa-edit mr-2" aria-hidden /> Edit
                                                </button>
                                                <button
                                                  type="button"
                                                  className="labelflow-dropdown-item labelflow-dropdown-item--danger"
                                                  onClick={() => {
                                                    setOpenDropdownId(null);
                                                    setDropdownAnchor(null);
                                                    swalConfirm(
                                                      "Delete version",
                                                      "Are you sure you want to delete this model version?",
                                                      async (result) => {
                                                        if (result?.isConfirmed) {
                                                          await handler.handleDeleteModelVersion({
                                                            model,
                                                            verion_id: ver._id,
                                                          });
                                                        }
                                                      },
                                                    );
                                                  }}
                                                >
                                                  <i className="fa fa-trash mr-2" aria-hidden /> Delete
                                                </button>
                                              </div>
                                          </DropdownPortal>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="model-mgmt-empty">
                      <p className="mb-0">No data available</p>
                      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => handleAddData(model)}>
                        <i className="fa fa-plus mr-1" aria-hidden /> Add Data
                      </button>
                    </div>
                  )}
                </div>
              </div>
          );
        })
      ) : (
        <div className="model-mgmt-empty">
          <p className="mb-0">No projects found.</p>
        </div>
      )}
      {liveModal.open && (
        <div
          className="modal fade show"
          tabIndex="-1"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isLiveStatus(liveModal.payload?.progress?.trainingStatus) ? (
                    <>
                      <i className="fa fa-broadcast-tower mr-2" aria-hidden />
                      Live Training Progress
                    </>
                  ) : (
                    <>
                      <i className="fa fa-file-alt mr-2" aria-hidden />
                      Training Progress
                    </>
                  )}
                </h5>
                <button type="button" className="close" onClick={handleCloseLiveView}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {liveModal.loading ? (
                  <div className="text-center py-4">
                    <i className="fa fa-spinner fa-spin fa-2x mb-3"></i>
                    <p className="mb-0">Fetching latest training progress…</p>
                  </div>
                ) : liveModal.error ? (
                  <div className="alert alert-danger" role="alert">
                    {liveModal.error}
                  </div>
                ) : (
                  <>
                    <div className="mb-3">
                      <h6 className="text-muted mb-1">Model</h6>
                      <p className="mb-0 font-weight-bold">
                        {liveModal.meta?.model?.model_name || liveModal.meta?.model?.modelName || "-"}
                      </p>
                    </div>
                    <div className="mb-3">
                      <h6 className="text-muted mb-1">Version</h6>
                      <p className="mb-1">
                        <strong>No:</strong>{" "}
                        {liveModal.meta?.version?.version ?? "-"}
                      </p>
                      <p className="mb-0">
                        <strong>Status:</strong>{" "}
                        {liveModal.payload?.progress?.trainingStatus ?? "-"}
                      </p>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Started:</strong>{" "}
                          {formatDateTime(liveModal.payload?.progress?.trainingStartedAt)}
                        </p>
                        <p className="mb-1">
                          <strong>Completed:</strong>{" "}
                          {formatDateTime(liveModal.payload?.progress?.trainingCompletedAt)}
                        </p>
                        <p className="mb-1">
                          <strong>Model Path:</strong>{" "}
                          <span className="text-break">
                            {liveModal.payload?.progress?.modelPath ?? "-"}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Training Error:</strong>{" "}
                          {liveModal.payload?.progress?.trainingError ?? "-"}
                        </p>
                        <p className="mb-1">
                          <strong>Job Status:</strong>{" "}
                          {liveModal.payload?.job?.status ?? "N/A"}
                        </p>
                        <p className="mb-1">
                          <strong>Job Started:</strong>{" "}
                          {formatDateTime(liveModal.payload?.job?.started_at)}
                        </p>
                        <p className="mb-0">
                          <strong>Job Updated:</strong>{" "}
                          {formatDateTime(liveModal.payload?.job?.updated_at)}
                        </p>
                      </div>
                    </div>
                    <hr />
                    <TrainingMetricsPanel
                      metrics={liveModal.payload?.progress?.performanceMetrics}
                      status={liveModal.payload?.progress?.trainingStatus}
                    />
                    <hr />
                    <h6 className="text-muted">Live Logs</h6>
                    <div className="training-log-area">
                      {liveLogsError ? (
                        <div className="text-danger small">{liveLogsError}</div>
                      ) : liveLogs.length === 0 ? (
                        <div className="text-muted small">
                          {isFetchingLogs ? "Loading logs..." : "No logs to display yet."}
                        </div>
                      ) : (
                        liveLogs.map((line, idx) => (
                          <div key={`${idx}-${line}`} className="log-line">
                            {line}
                          </div>
                        ))
                      )}
                      <div ref={liveLogsEndRef} />
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <span className="lf-metrics__count mr-auto">
                  {liveModal.loading
                    ? "Refreshing…"
                    : liveModal.fetchedAt
                      ? `Updated ${formatClockTime(liveModal.fetchedAt)}`
                      : ""}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleRefreshLiveView}
                  disabled={liveModal.loading}
                >
                  <i className={`fa fa-sync${liveModal.loading ? " fa-spin" : ""} mr-1`}></i>
                  Refresh
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseLiveView}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ModelPopup isModalOpen={isModalOpen} handleCloseModal={() => setIsModalOpen(false)} />

      {configModal.open && (
        <div
          className="modal fade show"
          tabIndex="-1"
          role="dialog"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa fa-cog mr-2" />
                  Training Configuration
                </h5>
                <button type="button" className="close" onClick={closeConfigModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <dl className="row mb-0">
                  <dt className="col-sm-4">Model Type</dt>
                  <dd className="col-sm-8">{configModal.modelType || "-"}</dd>
                  <dt className="col-sm-4">YOLO Variant</dt>
                  <dd className="col-sm-8">{configModalValues.modelVariant}</dd>
                  <dt className="col-sm-4">Epochs</dt>
                  <dd className="col-sm-8">{configModalValues.epochs}</dd>
                  <dt className="col-sm-4">Image Size</dt>
                  <dd className="col-sm-8">{configModalValues.imgsz}</dd>
                  <dt className="col-sm-4">Batch Size</dt>
                  <dd className="col-sm-8">{configModalValues.batchSize ?? "-"}</dd>
                  <dt className="col-sm-4">Stratified Split</dt>
                  <dd className="col-sm-8">{formatBooleanDisplay(configModalValues.stratify)}</dd>
                  <dt className="col-sm-4">Data Split</dt>
                  <dd className="col-sm-8">
                    Train {configModalValues.trainSplit ?? "-"}% / Val {configModalValues.valSplit ?? "-"}% / Test {configModalValues.testSplit ?? "-"}%
                  </dd>
                  <dt className="col-sm-4">Early Stopping Patience</dt>
                  <dd className="col-sm-8">
                    {configModalValues.patience !== "-" ? `${configModalValues.patience} epochs` : "-"}
                  </dd>
                  <dt className="col-sm-4">Run Directory</dt>
                  <dd className="col-sm-8">
                    {configModal.version?.trainingParams?.runDir ||
                      configModal.version?.runDir ||
                      "-"}
                  </dd>
                </dl>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeConfigModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ModelManagementMainSection;
